import { isSurveyExpiredError } from '@/shared-utilities';
import {
  InsightRecordingEventType,
  MessageSender,
  MessageV2,
  ProjectType,
  RuleActionType,
} from '@/survey-graphql';
import { toast } from '@/ui';

import {
  EmitNonPrimaryRecordingEventsParams,
  ErrorHandlerParams,
  IframeUrlParams,
  RecordingEventMessage,
  RecordingEventParams,
  RedirectActionsParams,
  VoiceModeCompletionParams,
} from './project-response-handler.types';
import { getRedirectUrl } from './project-response.helpers';

/**
 * Sentinel for the conversation-over button on a positive-sentiment review
 * redirect. The renderer maps it to the localized `Completion.leaveAReview`
 * label — it is never shown to a responder verbatim.
 */
export const CONVERSATION_OVER_REVIEW_LABEL = 'Leave a Review';

/**
 * `client-preset`'s `avoidOptionals: { field: true }` makes every operation
 * field `T | null` with no `undefined`, but the raw schema type these assets
 * come from (`QuestionAssetModel`/`QuestionOptionAssetModel`) leaves
 * `name`/`description` as `Maybe<string>` (`undefined` included) — so an
 * optimistic literal built from schema data needs the `undefined` coerced to
 * `null` to satisfy the operation type.
 */
export function normalizeAsset<
  T extends { name?: string | null; description?: string | null },
>(
  asset: T
): Omit<T, 'name' | 'description'> & {
  name: string | null;
  description: string | null;
} {
  return {
    ...asset,
    name: asset.name ?? null,
    description: asset.description ?? null,
  };
}

// Helper functions
export const createErrorHandler = ({
  error,
  setIsPublished,
  setSaving,
  setCurrentTranscript,
  unlockAudioRecord,
  responseMode,
  startListening,
  shouldLockAndUnlock = true,
}: ErrorHandlerParams) => {
  if (isSurveyExpiredError(error)) {
    setIsPublished(false);
  } else {
    toast.add({ title: error.message, type: 'error' });
  }

  if (setSaving) {
    setSaving(false);
  }

  if (setCurrentTranscript) {
    setCurrentTranscript('');
  }

  if (unlockAudioRecord && shouldLockAndUnlock) {
    unlockAudioRecord();
  }

  if (responseMode === 'voice' && startListening && shouldLockAndUnlock) {
    startListening();
  }
};

export const handleRecordingEvent = async ({
  projectType,
  insightId,
  messageId,
  time,
  type,
  addRecordingEvent,
}: RecordingEventParams) => {
  if (projectType === ProjectType.UsabilityTesting) {
    try {
      await addRecordingEvent({
        variables: {
          input: {
            insightId,
            messageId,
            time,
            type,
          },
        },
      });
    } catch {
      // Silently handle recording event errors to prevent crashes
    }
  }
};

/**
 * Classifies a message into the corresponding `InsightRecordingEventType` for
 * the **non-primary** event kinds emitted per-message during a UT response.
 * Returns `null` for primary-question assistant messages (handled by the
 * `handleRecordingEvent` PrimaryQuestion path) and for messages that should
 * not produce a timeline event (URL-content messages, conversation-over
 * markers, blank user messages).
 */
export const classifyNonPrimaryEventType = (
  message: RecordingEventMessage
): InsightRecordingEventType | null => {
  if (message.metadata?.isConversationOver === true) return null;
  if (message.contentUrl) return null;

  if (message.sender === MessageSender.Assistant) {
    return message.metadata?.assistantQuestion === true
      ? InsightRecordingEventType.AiFollowUp
      : null;
  }
  if (message.sender === MessageSender.User) {
    if (message.transcript && message.transcript.trim().length > 0) {
      return InsightRecordingEventType.UserVoiceResponse;
    }
    if (message.text && message.text.trim().length > 0) {
      return InsightRecordingEventType.UserTypedResponse;
    }
  }
  return null;
};

/**
 * Emits `addRecordingEvent` mutations for the three non-primary event kinds
 * (`AiFollowUp`, `UserVoiceResponse`, `UserTypedResponse`) carried by a batch
 * of messages just received from BE. Primary-question events flow through the
 * `handleRecordingEvent` PrimaryQuestion path; the classifier returns null for
 * those so this loop only emits the non-primary kinds.
 *
 * Errors are swallowed — a missed timestamp must never break the survey flow.
 */
export const emitNonPrimaryRecordingEvents = async ({
  projectType,
  insightId,
  messages,
  getCurrentRecordingTime,
  addRecordingEvent,
  voiceResponseTime = null,
}: EmitNonPrimaryRecordingEventsParams) => {
  if (projectType !== ProjectType.UsabilityTesting) return;
  for (const message of messages) {
    if (!message) continue;
    const type = classifyNonPrimaryEventType(message);
    if (!type) continue;
    // Voice answers commit ~seconds after the utterance (STT + network), so
    // the commit-time clock overshoots where the answer occurs in the
    // recording. Use the utterance-start time when supplied; everything else
    // (typed answers, AI follow-ups) keeps the commit-time clock.
    const time =
      type === InsightRecordingEventType.UserVoiceResponse &&
      voiceResponseTime !== null
        ? voiceResponseTime
        : getCurrentRecordingTime();
    try {
      await addRecordingEvent({
        variables: {
          input: {
            insightId,
            messageId: message.id,
            time,
            type,
          },
        },
      });
    } catch {
      // Silently handle event errors to prevent crashes
    }
  }
};

export const handleRedirectActions = ({
  actions,
  panel,
  redirectUrlRef,
  conversationOverRef,
  redirectActionRef,
  isConversationOver,
  onRuntimeRedirectSet,
}: RedirectActionsParams) => {
  const shouldHandleDefaultPanelRedirect =
    isConversationOver && panel?.provider;
  if (
    (!actions || !actions.postActivationActionType) &&
    !shouldHandleDefaultPanelRedirect
  )
    return;

  const redirectUrl = getRedirectUrl(
    actions?.postActivationActionType,
    actions?.postActivationActionData ?? '',
    panel,
    redirectUrlRef.current,
    isConversationOver
  );

  if (redirectUrl) {
    redirectUrlRef.current = redirectUrl;
    onRuntimeRedirectSet?.();
  }

  if (actions?.postActivationActionType === RuleActionType.ReviewRedirect) {
    conversationOverRef.current = CONVERSATION_OVER_REVIEW_LABEL;
    redirectActionRef.current = 'new-tab';
    onRuntimeRedirectSet?.();
  }
};

export const handleVoiceModeCompletion = ({
  messages,
  responseMode,
  setCurrentTranscript,
  unlockAudioRecord,
  resetAudioRecord,
  startListening,
  shouldLockAndUnlock = true,
}: VoiceModeCompletionParams) => {
  setCurrentTranscript('');

  if (unlockAudioRecord && shouldLockAndUnlock) {
    unlockAudioRecord();
  }

  if (resetAudioRecord && shouldLockAndUnlock) {
    resetAudioRecord();
  }

  const hasConversationOver = messages.some(
    (m) => m.metadata?.isConversationOver
  );
  if (!hasConversationOver && responseMode === 'voice' && shouldLockAndUnlock) {
    startListening();
  }
};

export const handleIframeUrl = ({
  reply,
  setIframeUrl,
  setMobileAppEmbedUrl,
}: IframeUrlParams) => {
  if (!reply) return;
  // Mobile App question: drive the Appetize iframe instead of the URL iframe
  if (reply.mobileAppEmbedUrl) {
    setMobileAppEmbedUrl(reply.mobileAppEmbedUrl);
    return;
  }
  if (reply.contentUrl && reply.text) {
    setIframeUrl(reply.contentUrl);
  }
};

export const sortMessagesByCreatedAt = (
  messages: MessageV2[],
  ascending = true
) => {
  return messages.toSorted((a, b) => {
    const timeA = new Date(a.createdAt).getTime();
    const timeB = new Date(b.createdAt).getTime();
    return ascending ? timeA - timeB : timeB - timeA;
  });
};
